import React, { useEffect, useMemo, useRef, useState } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import CalculatorToolsRail from '../components/CalculatorToolsRail';
import { inrAmountInWordsShort } from './calc_components/calculatorAmountWords';
import CalculatorPrecisionToggle from './calc_components/CalculatorPrecisionToggle';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import { formatYearsCount } from './calc_components/calculatorFormatters';
import { computeTooltipPlacement } from './calc_components/tooltipPositioning';
import { buildEmiYearlySplit } from './emiYearlySplitMath';
import EmiSplitChart from './EmiSplitChart';
import './calculators.css';
import './EmiInputBands.css';
import './WhereYourEmiReallyGoesCalculator.css';

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const minInterestRate = 4;
const maxInterestRate = 20;
const interestRateStep = 0.25;

const minTenureYears = 5;
const maxTenureYears = 30;

const minLoanAmount = 100000;
const maxLoanAmount = 1000000000;

/** Loan amount is required for EMI math (rate + tenure alone cannot size a payment). */
const STANDALONE_DEFAULTS = {
  interestRate: 9,
  tenureYears: 20,
  loanAmount: 10000000,
  focusYear: 20,
};

function formatInterestRatePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function snapInterestRate(n) {
  const raw = Number(n);
  if (!Number.isFinite(raw)) return STANDALONE_DEFAULTS.interestRate;
  const snapped =
    minInterestRate +
    Math.round((raw - minInterestRate) / interestRateStep) * interestRateStep;
  return Math.min(
    maxInterestRate,
    Math.max(minInterestRate, Number(snapped.toFixed(2))),
  );
}

function formatPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n)}%`;
}

function MetricTip({ id, label, tooltip, children }) {
  const tip = tooltip != null && String(tooltip).trim() !== '';
  const tooltipId = `${id}-tip`;
  const tooltipWrapRef = useRef(null);
  const [tooltipPosClass, setTooltipPosClass] = useState('');
  const [tooltipInlineStyle, setTooltipInlineStyle] = useState(undefined);

  function refreshPlacement() {
    const placement = computeTooltipPlacement(tooltipWrapRef.current);
    setTooltipPosClass(placement.tooltipPosClass);
    setTooltipInlineStyle(placement.tooltipInlineStyle);
  }

  return (
    <div className="emi-goes__metric">
      <div className="emi-goes__metric-label-row">
        <p className="emi-goes__metric-label">{label}</p>
        {tip ? (
          <span
            ref={tooltipWrapRef}
            className={`nps-ups-tooltip-wrap emi-goes__metric-tip ${tooltipPosClass}`.trim()}
            onMouseEnter={refreshPlacement}
            onFocus={refreshPlacement}
          >
            <button
              type="button"
              className="btn-small-round nps-ups-q"
              aria-label={`About ${label}`}
              aria-describedby={tooltipId}
            >
              ?
            </button>
            <span
              className="nps-ups-tooltip-bubble"
              id={tooltipId}
              role="tooltip"
              style={tooltipInlineStyle}
            >
              {tooltip}
            </span>
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function cumulativeThroughYear(yearRows, focusYear) {
  const rows = Array.isArray(yearRows) ? yearRows : [];
  const through = Math.max(1, Number(focusYear) || 1);
  let interest = 0;
  let principal = 0;
  for (const row of rows) {
    if (row.year > through) break;
    interest += row.interest;
    principal += row.principal;
  }
  const total = interest + principal;
  return {
    interest,
    principal,
    total,
    interestSharePct: total > 0 ? (interest / total) * 100 : 0,
    principalSharePct: total > 0 ? (principal / total) * 100 : 0,
  };
}

export default function WhereYourEmiReallyGoesCalculator({ embed = false }) {
  useEffect(() => {
    if (!embed) window.scrollTo(0, 0);
  }, [embed]);

  const [interestRate, setInterestRate] = useState(STANDALONE_DEFAULTS.interestRate);
  const [tenureYears, setTenureYears] = useState(STANDALONE_DEFAULTS.tenureYears);
  const [loanAmount, setLoanAmount] = useState(STANDALONE_DEFAULTS.loanAmount);
  const [loanAmountForWords, setLoanAmountForWords] = useState(
    STANDALONE_DEFAULTS.loanAmount,
  );
  const [focusYear, setFocusYear] = useState(STANDALONE_DEFAULTS.focusYear);

  const split = useMemo(
    () => buildEmiYearlySplit(loanAmount, interestRate, tenureYears),
    [loanAmount, interestRate, tenureYears],
  );

  // Keep focus year inside the current tenure when tenure shrinks.
  useEffect(() => {
    setFocusYear((prev) => Math.min(Math.max(1, prev), tenureYears));
  }, [tenureYears]);

  const cumulative = useMemo(
    () => cumulativeThroughYear(split.yearRows, focusYear),
    [split.yearRows, focusYear],
  );

  const { formatInr, resolveAmount } = useCalcAmountDisplayMode();

  const loanAmountWords =
    loanAmountForWords > 0
      ? inrAmountInWordsShort(Math.round(loanAmountForWords))
      : '';
  const monthlyEmiWords =
    split.monthlyEmi > 0
      ? inrAmountInWordsShort(resolveAmount(split.monthlyEmi))
      : '';

  function handleStartOver(e) {
    e.preventDefault();
    setInterestRate(STANDALONE_DEFAULTS.interestRate);
    setTenureYears(STANDALONE_DEFAULTS.tenureYears);
    setLoanAmount(STANDALONE_DEFAULTS.loanAmount);
    setLoanAmountForWords(STANDALONE_DEFAULTS.loanAmount);
    setFocusYear(STANDALONE_DEFAULTS.focusYear);
  }

  function handleTenureChange(v) {
    const next = Math.min(maxTenureYears, Math.max(minTenureYears, Math.round(Number(v))));
    setTenureYears(next);
    setFocusYear((prev) => Math.min(prev, next));
  }

  const calculatorCard = (
    <div
      className={`appContainer nps-ups--compact emi-calculator where-emi-goes${
        embed ? ' nps-ups--embed' : ''
      }`}
    >
      <h1 className="tool-heading">Where Your EMI Really Goes</h1>

      <div className="col-light-grey italic font-size-small nps-ups-intro">
        <p>
          See how each year’s EMI splits into interest versus principal—and why the early
          years barely shrink your loan balance.
        </p>
      </div>

      <div className="emi-goes__results" aria-live="polite">
        <CalculatorPrecisionToggle className="calc-precision-toggle--inline" />
        <div className="emi-goes__metrics">
          <MetricTip
            id="emi-goes-cum-total-emi"
            label={`Total EMI paid in ${focusYear} ${focusYear === 1 ? 'year' : 'years'}`}
            tooltip="Sum of every EMI from year 1 through the selected year (interest plus principal)."
          >
            <p className="emi-goes__metric-value emi-goes__metric-value--interest">
              {formatInr(cumulative.total)}
            </p>
            <p className="emi-goes__metric-sub">
              {formatPct(cumulative.interestSharePct)} was interest
            </p>
          </MetricTip>

          <MetricTip
            id="emi-goes-cum-principal"
            label="Total principal repaid"
            tooltip="Sum of the principal portion of every EMI from year 1 through the selected year—the amount that has actually reduced your loan balance."
          >
            <p className="emi-goes__metric-value emi-goes__metric-value--principal">
              {formatInr(cumulative.principal)}
            </p>
            <p className="emi-goes__metric-sub">
              {formatPct(cumulative.principalSharePct)} of EMI paid so far
            </p>
          </MetricTip>
        </div>

        <section className="emi-goes__year-slider">
          <EngagerSliderRow
            id="emi-goes-focus-year"
            label="Show progress through year"
            tooltip="Slide to a loan year to see cumulative interest vs principal paid so far. Bars up to that year stay highlighted on the chart."
            value={focusYear}
            setValue={(v) =>
              setFocusYear(Math.min(tenureYears, Math.max(1, Math.round(Number(v)))))
            }
            min={1}
            max={tenureYears}
            step={1}
            format={(v) => `Year ${Math.round(Number(v))}`}
            variant="standard"
            defaultValue={STANDALONE_DEFAULTS.focusYear}
          />
        </section>
      </div>

      <EmiSplitChart
        yearRows={split.yearRows}
        highlightThroughYear={focusYear}
        title="Annual EMI: interest vs principal by year"
      />

      <div className="border-top-light nps-ups-divider" />

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="emi-goes-interest-rate"
          label="Annual interest rate"
          tooltip="Yearly interest rate charged on the outstanding loan balance. Move in 0.25% steps between 4% and 20%."
          value={interestRate}
          setValue={(v) => setInterestRate(snapInterestRate(v))}
          min={minInterestRate}
          max={maxInterestRate}
          step={interestRateStep}
          format={formatInterestRatePct}
          variant="inr"
          defaultValue={STANDALONE_DEFAULTS.interestRate}
        />
      </section>

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="emi-goes-tenure-years"
          label="Loan tenure"
          tooltip="Total repayment period in whole years (5–30). Longer tenures keep early EMIs more interest-heavy."
          value={tenureYears}
          setValue={handleTenureChange}
          min={minTenureYears}
          max={maxTenureYears}
          step={1}
          format={formatYearsCount}
          variant="inr"
          defaultValue={STANDALONE_DEFAULTS.tenureYears}
        />
      </section>

      <section className="emi-input-band emi-input-band--b">
        <div className="savings-goal-first-year-exp-field">
          <EngagerSliderRow
            id="emi-goes-loan-amount"
            label="Loan amount"
            tooltip="Principal borrowed. Needed to size the EMI in rupees; changing it scales the chart amounts but the interest-vs-principal pattern stays similar for the same rate and tenure."
            value={Math.min(loanAmount, maxLoanAmount)}
            setValue={(v) => {
              const next = Math.min(maxLoanAmount, Math.max(minLoanAmount, v));
              setLoanAmount(next);
              setLoanAmountForWords(next);
            }}
            onLiveValueChange={(n) => {
              if (n != null && Number.isFinite(n)) {
                setLoanAmountForWords(Math.min(maxLoanAmount, Math.max(0, n)));
              } else {
                setLoanAmountForWords(0);
              }
            }}
            min={minLoanAmount}
            max={maxLoanAmount}
            step={100000}
            format={(v) => formatterINR.format(v)}
            variant="inr"
            defaultValue={STANDALONE_DEFAULTS.loanAmount}
          />
          {loanAmountWords ? (
            <p className="calc-output-amount-in-words savings-goal-first-year-exp-words">
              {loanAmountWords}
            </p>
          ) : null}
        </div>
      </section>

      <div className="emi-goes__monthly-emi" aria-live="polite">
        <div className="emi-goes__monthly-emi-label-row">
          <span className="emi-goes__monthly-emi-label">Monthly EMI</span>
          <span
            className="nps-ups-tooltip-wrap emi-goes__monthly-emi-tip"
            title="Fixed equated monthly instalment from your loan amount, rate, and tenure."
          >
            <button
              type="button"
              className="btn-small-round nps-ups-q"
              aria-label="About monthly EMI"
            >
              ?
            </button>
            <span className="nps-ups-tooltip-bubble" role="tooltip">
              Fixed equated monthly instalment from your loan amount, annual interest rate,
              and tenure using the standard amortisation formula. This field is calculated
              automatically.
            </span>
          </span>
        </div>
        <p className="emi-goes__monthly-emi-value">
          {formatInr(split.monthlyEmi)}
        </p>
        {monthlyEmiWords ? (
          <p className="calc-output-amount-in-words emi-goes__monthly-emi-words">
            {monthlyEmiWords}
          </p>
        ) : null}
      </div>

      <button type="button" className="calc-start-over-btn" onClick={handleStartOver}>
        Start over
      </button>

      <p className="font-small font-light-grey italic margin-0 nps-ups-footnote">
        Illustrative amortisation only—fixed rate, no fees, prepayments, or tax effects. Not
        lender or product advice.
      </p>
    </div>
  );

  if (embed) return calculatorCard;

  return (
    <div className="calculator-page-layout calculator-page-layout--emi-goes">
      {calculatorCard}
      <div className="calculator-rail-shell">
        <CalculatorToolsRail currentToolKey="where-your-emi-really-goes" />
      </div>
    </div>
  );
}
